/**
 * Audit Record Manager – QMS Phase 3
 * List, create, edit audit records linked to standards or protocols.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, FileCheck, AlertCircle } from 'lucide-react';
import { auditRecordService, AUDIT_TYPES, RESULTS } from '../../services/auditRecordService';
import processService from '../../services/processService';
import { protocolService } from '../../services/protocolService';
import './AuditRecordManager.css';

const AuditRecordManager = ({ organizationId, userId }) => {
  const { t } = useTranslation(['qms', 'dashboards']);
  const [records, setRecords] = useState([]);
  const [standards, setStandards] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [scopeFilter, setScopeFilter] = useState(''); // 'standard' | 'protocol' | ''
  const [processFilter, setProcessFilter] = useState('');
  const [protocolFilter, setProtocolFilter] = useState('');
  const [form, setForm] = useState({
    processId: '',
    protocolId: '',
    auditDate: new Date().toISOString().slice(0, 10),
    auditorName: '',
    auditType: 'internal',
    scope: '',
    result: 'pass',
    findings: '',
    correctiveActionsRequired: '',
    nextAuditDue: '',
    notes: ''
  });

  const loadRecords = async () => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    try {
      const opts = {};
      if (processFilter) opts.processId = processFilter;
      else if (protocolFilter) opts.protocolId = protocolFilter;
      const data = await auditRecordService.getByOrganization(organizationId, opts);
      setRecords(data);
    } catch (err) {
      console.error('Load audit records:', err);
      setError(err.message);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStandards = async () => {
    if (!organizationId) return;
    try {
      const data = await processService.getProcesses(organizationId);
      setStandards(data || []);
    } catch (_) {}
  };

  const loadProtocols = async () => {
    try {
      const data = await protocolService.getAllProtocols({});
      setProtocols(data || []);
    } catch (_) {}
  };

  useEffect(() => {
    loadStandards();
    loadProtocols();
  }, [organizationId]);

  useEffect(() => {
    loadRecords();
  }, [organizationId, processFilter, protocolFilter]);

  const resetForm = () => {
    setForm({
      processId: '',
      protocolId: '',
      auditDate: new Date().toISOString().slice(0, 10),
      auditorName: '',
      auditType: 'internal',
      scope: '',
      result: 'pass',
      findings: '',
      correctiveActionsRequired: '',
      nextAuditDue: '',
      notes: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const openEdit = (r) => {
    setForm({
      processId: r.processId || '',
      processCode: r.processCode || '',
      processName: r.processName || '',
      protocolId: r.protocolId || '',
      protocolName: r.protocolName || '',
      auditDate: r.auditDate ? (typeof r.auditDate === 'string' ? r.auditDate.slice(0, 10) : r.auditDate.toISOString?.().slice(0, 10)) : '',
      auditorName: r.auditorName || '',
      auditType: r.auditType || 'internal',
      scope: r.scope || '',
      result: r.result || 'pass',
      findings: r.findings || '',
      correctiveActionsRequired: r.correctiveActionsRequired || '',
      nextAuditDue: r.nextAuditDue ? (typeof r.nextAuditDue === 'string' ? r.nextAuditDue.slice(0, 10) : r.nextAuditDue.toISOString?.().slice(0, 10)) : '',
      notes: r.notes || ''
    });
    setEditingId(r.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!organizationId || !userId) return;
    const std = standards.find((s) => s.id === form.processId);
    const prot = protocols.find((p) => (p.firestoreId || p.id) === form.protocolId);
    const payload = {
      organizationId,
      processId: form.processId || null,
      processCode: std?.processCode || form.processCode || null,
      processName: std?.name || form.processName || null,
      protocolId: form.protocolId || null,
      protocolName: prot?.name || form.protocolName || null,
      auditDate: form.auditDate,
      auditorName: form.auditorName,
      auditType: form.auditType,
      scope: form.scope,
      result: form.result,
      findings: form.findings,
      correctiveActionsRequired: form.correctiveActionsRequired,
      nextAuditDue: form.nextAuditDue || null,
      notes: form.notes
    };
    try {
      if (editingId) {
        await auditRecordService.update(editingId, payload, userId);
      } else {
        await auditRecordService.create(payload, userId);
      }
      resetForm();
      loadRecords();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await auditRecordService.delete(deleteConfirm);
      setDeleteConfirm(null);
      loadRecords();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const dt = typeof d === 'string' ? new Date(d) : d;
    return dt.toLocaleDateString();
  };

  const isOverdue = (r) => r.nextAuditDue && new Date(r.nextAuditDue) < new Date();

  return (
    <div className="audit-record-manager">
      <div className="arm-header">
        <h2>{t('qms:auditRecords') || 'Audit Records'}</h2>
        <div className="arm-actions">
          <select value={scopeFilter} onChange={(e) => { setScopeFilter(e.target.value); setProcessFilter(''); setProtocolFilter(''); }}>
            <option value="">{t('qms:allScopes') || 'All scopes'}</option>
            <option value="standard">{t('qms:byStandard') || 'By standard'}</option>
            <option value="protocol">{t('qms:byProtocol') || 'By protocol'}</option>
          </select>
          {scopeFilter === 'standard' && (
            <select value={processFilter} onChange={(e) => setProcessFilter(e.target.value)} className="arm-filter">
              <option value="">{t('qms:allStandards') || 'All standards'}</option>
              {standards.map((s) => (
                <option key={s.id} value={s.id}>{s.processCode} – {s.name}</option>
              ))}
            </select>
          )}
          {scopeFilter === 'protocol' && (
            <select value={protocolFilter} onChange={(e) => setProtocolFilter(e.target.value)} className="arm-filter">
              <option value="">{t('qms:allProtocols') || 'All protocols'}</option>
              {protocols.map((p) => (
                <option key={p.firestoreId || p.id} value={p.firestoreId || p.id}>{p.name}</option>
              ))}
            </select>
          )}
          <button type="button" className="btn btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>
            <Plus size={18} /> {t('qms:addAudit') || 'Add audit'}
          </button>
        </div>
      </div>

      {error && <div className="arm-error">{error}</div>}

      {loading ? (
        <p>{t('qms:loading') || 'Loading…'}</p>
      ) : records.length === 0 ? (
        <div className="arm-empty">
          <p>{t('qms:noAuditsYet') || 'No audit records yet. Add audits to track internal, ANMCS, and external verification.'}</p>
          <button type="button" className="btn btn-primary" onClick={() => setShowForm(true)}>
            <Plus size={18} /> {t('qms:addAudit') || 'Add audit'}
          </button>
        </div>
      ) : (
        <div className="arm-table-wrap">
          <table className="arm-table">
            <thead>
              <tr>
                <th>{t('qms:auditDate') || 'Date'}</th>
                <th>{t('qms:scope') || 'Scope'}</th>
                <th>{t('qms:auditor') || 'Auditor'}</th>
                <th>{t('qms:type') || 'Type'}</th>
                <th>{t('qms:result') || 'Result'}</th>
                <th>{t('qms:nextDue') || 'Next due'}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id} className={isOverdue(r) ? 'overdue' : ''}>
                  <td>{formatDate(r.auditDate)}</td>
                  <td>{r.scope || r.processCode || r.protocolName || '—'}</td>
                  <td>{r.auditorName || '—'}</td>
                  <td>{r.auditType}</td>
                  <td>
                    <span className={`result-badge ${r.result}`}>
                      {r.result === 'pass' && <FileCheck size={14} />}
                      {r.result !== 'pass' && <AlertCircle size={14} />}
                      {r.result}
                    </span>
                  </td>
                  <td>
                    {r.nextAuditDue ? (
                      <span className={isOverdue(r) ? 'overdue-date' : ''}>
                        {formatDate(r.nextAuditDue)}
                        {isOverdue(r) && ' (overdue)'}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    <button type="button" className="btn-icon" onClick={() => openEdit(r)} title={t('qms:edit') || 'Edit'}>
                      <Edit size={16} />
                    </button>
                    <button type="button" className="btn-icon danger" onClick={() => setDeleteConfirm(r.id)} title={t('qms:delete') || 'Delete'}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="arm-modal-overlay" onClick={() => resetForm()}>
          <div className="arm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? (t('qms:editAudit') || 'Edit audit') : (t('qms:addAudit') || 'Add audit')}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row two-col">
                <div>
                  <label>{t('qms:standard') || 'Standard'}</label>
                  <select value={form.processId} onChange={(e) => setForm({ ...form, processId: e.target.value, protocolId: '' })}>
                    <option value="">—</option>
                    {standards.map((s) => (
                      <option key={s.id} value={s.id}>{s.processCode} – {s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label>{t('qms:protocol') || 'Protocol'}</label>
                  <select value={form.protocolId} onChange={(e) => setForm({ ...form, protocolId: e.target.value, processId: '' })}>
                    <option value="">—</option>
                    {protocols.map((p) => (
                      <option key={p.firestoreId || p.id} value={p.firestoreId || p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <label>{t('qms:scope') || 'Scope (description)'}</label>
                <input type="text" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} placeholder="e.g. Monthly hand hygiene compliance audit" />
              </div>
              <div className="form-row two-col">
                <div>
                  <label>{t('qms:auditDate') || 'Audit date'}</label>
                  <input type="date" value={form.auditDate} onChange={(e) => setForm({ ...form, auditDate: e.target.value })} required />
                </div>
                <div>
                  <label>{t('qms:auditor') || 'Auditor'}</label>
                  <input type="text" value={form.auditorName} onChange={(e) => setForm({ ...form, auditorName: e.target.value })} />
                </div>
              </div>
              <div className="form-row two-col">
                <div>
                  <label>{t('qms:auditType') || 'Type'}</label>
                  <select value={form.auditType} onChange={(e) => setForm({ ...form, auditType: e.target.value })}>
                    {AUDIT_TYPES.map((at) => (
                      <option key={at} value={at}>{t(`qms:auditType_${at}`) || at}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label>{t('qms:result') || 'Result'}</label>
                  <select value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })}>
                    {RESULTS.map((res) => (
                      <option key={res} value={res}>{t(`qms:result_${res}`) || res.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <label>{t('qms:findings') || 'Findings'}</label>
                <textarea value={form.findings} onChange={(e) => setForm({ ...form, findings: e.target.value })} rows={3} />
              </div>
              <div className="form-row">
                <label>{t('qms:correctiveActions') || 'Corrective actions required'}</label>
                <textarea value={form.correctiveActionsRequired} onChange={(e) => setForm({ ...form, correctiveActionsRequired: e.target.value })} rows={2} />
              </div>
              <div className="form-row">
                <label>{t('qms:nextAuditDue') || 'Next audit due'}</label>
                <input type="date" value={form.nextAuditDue} onChange={(e) => setForm({ ...form, nextAuditDue: e.target.value })} />
              </div>
              <div className="form-row">
                <label>{t('qms:notes') || 'Notes'}</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>{t('qms:cancel') || 'Cancel'}</button>
                <button type="submit" className="btn btn-primary">{editingId ? (t('qms:save') || 'Save') : (t('qms:add') || 'Add')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="arm-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="arm-modal arm-confirm" onClick={(e) => e.stopPropagation()}>
            <p>{t('qms:confirmDeleteAudit') || 'Delete this audit record?'}</p>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>{t('qms:cancel') || 'Cancel'}</button>
              <button type="button" className="btn btn-danger" onClick={handleDelete}>{t('qms:delete') || 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditRecordManager;
